import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/page-nav.default.js';
import * as wireframeSkin from './skins/page-nav.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

class RunPageNav extends RunElement {
  #prevLabel = '';
  #prevHref = '';
  #nextLabel = '';
  #nextHref = '';
  #rendering = false;
  #clickController = null;

  static get observedAttributes() {
    return ['prev-label', 'prev-href', 'next-label', 'next-href'];
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'navigation');
    this.setAttribute('aria-label', 'Component navigation');
    this.#attachClickHandler();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clickController?.abort();
    this.#clickController = null;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'prev-label': this.prevLabel = newValue !== null ? newValue : ''; break;
      case 'prev-href':  this.prevHref  = newValue !== null ? newValue : ''; break;
      case 'next-label': this.nextLabel = newValue !== null ? newValue : ''; break;
      case 'next-href':  this.nextHref  = newValue !== null ? newValue : ''; break;
    }
  }

  get prevLabel() { return this.#prevLabel; }
  set prevLabel(value) {
    const coerced = String(value == null ? '' : value).trim();
    if (this.#prevLabel === coerced) return;
    this.#prevLabel = coerced;
    this.setAttribute('prev-label', coerced);
    this.requestUpdate();
  }

  get prevHref() { return this.#prevHref; }
  set prevHref(value) {
    const coerced = String(value == null ? '' : value).trim();
    if (this.#prevHref === coerced) return;
    this.#prevHref = coerced;
    this.setAttribute('prev-href', coerced);
    this.requestUpdate();
  }

  get nextLabel() { return this.#nextLabel; }
  set nextLabel(value) {
    const coerced = String(value == null ? '' : value).trim();
    if (this.#nextLabel === coerced) return;
    this.#nextLabel = coerced;
    this.setAttribute('next-label', coerced);
    this.requestUpdate();
  }

  get nextHref() { return this.#nextHref; }
  set nextHref(value) {
    const coerced = String(value == null ? '' : value).trim();
    if (this.#nextHref === coerced) return;
    this.#nextHref = coerced;
    this.setAttribute('next-href', coerced);
    this.requestUpdate();
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  #attachClickHandler() {
    this.#clickController?.abort();
    this.#clickController = new AbortController();
    this.shadowRoot.addEventListener('click', (e) => {
      const link = e.target.closest?.('.pn-link');
      if (!link) return;
      const direction = link.classList.contains('pn-prev') ? 'prev' : 'next';
      const href = link.getAttribute('href') || '';
      const navEvent = new CustomEvent('run-page-nav-navigate', {
        bubbles: true,
        composed: true,
        cancelable: true,
        detail: { direction, href },
      });
      const cancelled = !this.dispatchEvent(navEvent);
      if (cancelled) e.preventDefault();
    }, { signal: this.#clickController.signal });
  }
}

if (!customElements.get('run-page-nav')) {
  customElements.define('run-page-nav', RunPageNav);
}

export { RunPageNav };
export default RunPageNav;
